import { KeyboardEvent, useEffect, useRef, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import { ChatMode } from '../../core/types/chat.types'
import { getAcceptString } from '../../core/utils/attachment'
import useMaxAttachmentStore, {
  areAttachmentsReadyToSend,
  areAttachmentsUploading,
  hasStagedAttachments,
} from '../../stores/use-max-attachment-store'

type MessageInputProps = {
  mode: ChatMode
  textDisabled?: boolean
  attachmentsDisabled?: boolean
  helperMessage?: string
  preserveStagedAttachmentsWhenDisabled?: boolean
  onSend: (content: string) => void
  onAttachFiles: (files: FileList | File[]) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    fontFamily: "'Poppins', sans-serif",
  },
  inputShell: {
    display: 'flex',
    gap: theme.spacing(1.5),
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    borderRadius: 16,
    padding: theme.spacing(2.5, 1.5, 1.5, 1.5),
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
    '&:focus-within': {
      borderColor: 'rgba(244, 125, 107, 0.5)',
      boxShadow: '0 0 0 2px rgba(244, 125, 107, 0.12)',
    },
  },
  textarea: {
    flex: 1,
    border: 0,
    outline: 'none',
    resize: 'none',
    background: 'transparent',
    minHeight: 24,
    maxHeight: 128,
    fontSize: '1rem',
    lineHeight: 1.5,
    color: 'var(--ds-fg-primary, #1f2430)',
    fontFamily: "'Poppins', sans-serif",
    '&::placeholder': {
      color: 'var(--ds-fg-tertiary, #8c92a5)',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
  },
  srOnlyInput: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    border: 0,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: 0,
    outline: 0,
    fontSize: '1.1rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 200ms ease',
    flexShrink: 0,
    cursor: 'pointer',
    fontFamily: "'Poppins', sans-serif",
  },
  attachButtonEnabled: {
    color: 'var(--ds-fg-secondary, #647089)',
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
    '&:hover': {
      backgroundColor: 'var(--ds-bg-subtle-hover, #e7eaf1)',
      color: 'var(--ds-fg-primary, #1f2430)',
    },
  },
  attachButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
  },
  sendButtonBase: {
    color: 'var(--ds-fg-primary, #1f2430)',
    fontWeight: 600,
  },
  sendButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  sendButtonEnabledWithAttach: {
    background: 'linear-gradient(135deg, #f47d6b 0%, #f68778 100%)',
    boxShadow: '0 0 20px rgba(244, 125, 107, 0.5)',
    color: '#ffffff',
  },
  sendButtonEnabledNoAttach: {
    background: 'linear-gradient(to right, #1b2645 0%, #2f3f69 100%)',
    boxShadow: '0 0 20px rgba(27, 38, 69, 0.5)',
    color: '#ffffff',
  },
  hint: {
    marginTop: theme.spacing(1),
    minHeight: 20,
    paddingLeft: theme.spacing(0.25),
    fontFamily: "'Poppins', sans-serif",
  },
  hintText: {
    fontSize: '0.75rem',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    lineHeight: 1.5,
    margin: 0,
  },
  hintEmphasis: {
    color: 'var(--ds-fg-secondary, #647089)',
  },
  '@media (max-width:960px)': {
    textarea: { fontSize: '0.95rem' },
  },
  '@media (max-width:600px)': {
    inputShell: { padding: theme.spacing(1.75, 1.25, 1.25, 1.25) },
    iconButton: { width: 34, height: 34 },
    hintText: { fontSize: '0.7rem' },
  },
}))

const MessageInput = ({
  mode,
  textDisabled = false,
  attachmentsDisabled = false,
  helperMessage,
  preserveStagedAttachmentsWhenDisabled = false,
  onSend,
  onAttachFiles,
}: MessageInputProps) => {
  const classes = useStyles()
  const [draft, setDraft] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const attachments = useMaxAttachmentStore((state) => state.attachments)
  const clearAll = useMaxAttachmentStore((state) => state.clearAll)

  const hasAttachments = hasStagedAttachments(attachments)
  const isUploading = areAttachmentsUploading(attachments)
  const isReadyToSend = areAttachmentsReadyToSend(attachments)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 128)}px`
    }
  }, [draft])

  useEffect(() => {
    if (attachmentsDisabled && !preserveStagedAttachmentsWhenDisabled) {
      clearAll()
    }
  }, [attachmentsDisabled, preserveStagedAttachmentsWhenDisabled, clearAll])

  const canSend = (() => {
    // Every chat message needs text; attachments only add a readiness requirement.
    if (textDisabled || draft.trim().length === 0) return false

    if (!attachmentsDisabled && hasAttachments) return isReadyToSend

    return true
  })()

  const sendButtonClass = (() => {
    if (!canSend) return `${classes.iconButton} ${classes.sendButtonBase} ${classes.sendButtonDisabled}`
    if (!attachmentsDisabled) return `${classes.iconButton} ${classes.sendButtonBase} ${classes.sendButtonEnabledWithAttach}`
    return `${classes.iconButton} ${classes.sendButtonBase} ${classes.sendButtonEnabledNoAttach}`
  })()

  const handleSend = () => {
    if (!canSend) return
    onSend(draft)
    setDraft('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const handleAttachClick = () => {
    if (attachmentsDisabled) return
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      onAttachFiles(files)
      event.target.value = ''
    }
  }

  const showUploadingStatus = !attachmentsDisabled && hasAttachments && isUploading

  return (
    <div className={classes.root}>
      <input
        ref={fileInputRef}
        className={classes.srOnlyInput}
        type="file"
        multiple
        accept={getAcceptString()}
        disabled={attachmentsDisabled}
        onChange={handleFileChange}
      />

      <div className={classes.inputShell}>
        <textarea
          ref={textareaRef}
          className={classes.textarea}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask me anything..."
          disabled={textDisabled}
          onKeyDown={handleKeyDown}
        />

        <div className={classes.actions}>
          <button
            type="button"
            className={`${classes.iconButton} ${
              attachmentsDisabled ? classes.attachButtonDisabled : classes.attachButtonEnabled
            }`}
            disabled={attachmentsDisabled}
            onClick={handleAttachClick}
            aria-label="Attach file"
          >
            📎
          </button>
          <button
            type="button"
            className={sendButtonClass}
            disabled={!canSend}
            onClick={handleSend}
            aria-label="Send message"
          >
            ➤
          </button>
        </div>
      </div>

      <div className={classes.hint}>
        <p className={classes.hintText}>
          {showUploadingStatus ? (
            <span className={classes.hintEmphasis}>
              Uploading attachments… please wait until uploads finish to send.{' '}
            </span>
          ) : helperMessage ? (
            <span className={classes.hintEmphasis}>{helperMessage} </span>
          ) : mode !== 'DEFAULT' ? (
            <span className={classes.hintEmphasis}>
              Use the options in the assistant message to continue the flow.{' '}
            </span>
          ) : attachmentsDisabled ? (
            <span>Attachments are disabled for this mode. </span>
          ) : null}
          The AI model may not always provide accurate or relevant information. Please verify your
          results as needed.
        </p>
      </div>
    </div>
  )
}

export default MessageInput
